import type { CSSProperties } from 'react';

import { InformationCircleIcon, TrashIcon } from '@heroicons/react/24/outline';

import type { CustomAttribute } from '@/types';
import { cn } from '@/utils/cn';

interface TagFilterAttributes {
  borderRadius?: CSSProperties['borderRadius'];
  borderWidth?: CSSProperties['borderWidth'];
  borderStyle?: CSSProperties['borderStyle'];
  borderColor?: CSSProperties['borderColor'];
  textColor?: CSSProperties['color'];
  backgroundColor?: CSSProperties['backgroundColor'];
  activeBackgroundColor?: CSSProperties['backgroundColor'];
  activeTextColor?: CSSProperties['color'];
}

interface TagFilterProps {
  tags: Array<{ id: string; display: string }>;
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  attributes: TagFilterAttributes;
  customAttributes?: CustomAttribute;
  contentTagsMatch?: 'any' | 'all';
  onContentTagsMatchChange?: (match: 'any' | 'all') => void;
  translations?: Record<string, string>;
}

const TagFilter = ({
  tags,
  selectedTags,
  onTagsChange,
  attributes,
  customAttributes,
  contentTagsMatch = 'any',
  onContentTagsMatchChange,
  translations,
}: TagFilterProps) => {
  const clearFiltersLabel = translations?.['search.posts.clear_filters'] ?? 'Clear filters';
  const matchLabel = translations?.['search.posts.match_label'] ?? 'Match:';
  const matchAnyLabel = translations?.['search.posts.match_any'] ?? 'Any tag';
  const matchAllLabel = translations?.['search.posts.match_all'] ?? 'All tags';
  const matchTooltipAny =
    translations?.['search.posts.match_tooltip_any'] ?? 'posts matching at least one selected tag';
  const matchTooltipAll = translations?.['search.posts.match_tooltip_all'] ?? 'posts matching every selected tag';
  const matchAriaLabel = translations?.['search.posts.match_aria_label'] ?? 'Tag matching mode explanation';
  // Page-context elements (Clear filters, Match label, info icon) sit on the
  // body background, not on a chip, so they should follow the theme's body
  // text color rather than the tag button text color.
  const metaTextColor = 'var(--wt-text-on-background-color, currentColor)';
  const handleTagClick = (tagDisplay: string) => {
    const isSelected = selectedTags.includes(tagDisplay);

    if (isSelected) {
      // Remove tag
      onTagsChange(selectedTags.filter((t) => t !== tagDisplay));
    } else {
      // Add tag
      onTagsChange([...selectedTags, tagDisplay]);
    }
  };

  const handleClearFilters = () => {
    onTagsChange([]);
  };

  if (!tags || tags.length === 0) {
    return null;
  }

  const hasSelectedTags = selectedTags.length > 0;
  const hasMultipleTags = selectedTags.length > 1;

  return (
    <div
      className={cn('flex flex-col gap-2 w-full', customAttributes?.className)}
      style={customAttributes?.style || {}}
    >
      {/* Tag buttons */}
      <div className="flex flex-wrap gap-2">
        {tags.map((tag) => {
          const isSelected = selectedTags.includes(tag.display);

          return (
            <button
              key={tag.id}
              type="button"
              onClick={() => handleTagClick(tag.display)}
              className="cursor-pointer px-4 py-1.5 text-xs transition-colors duration-150"
              style={{
                borderRadius: attributes.borderRadius,
                borderWidth: attributes.borderWidth,
                borderStyle: attributes.borderStyle,
                borderColor: attributes.borderColor,
                color: isSelected ? attributes.activeTextColor : attributes.textColor,
                backgroundColor: isSelected ? attributes.activeBackgroundColor : attributes.backgroundColor,
              }}
            >
              {tag.display}
            </button>
          );
        })}
      </div>

      {/* Clear filters and match mode toggle */}
      {hasSelectedTags && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleClearFilters}
            className="flex items-center gap-1 cursor-pointer px-2 py-1 text-xs w-fit transition-colors duration-150"
            style={{
              borderRadius: attributes.borderRadius,
              borderWidth: attributes.borderWidth,
              borderStyle: attributes.borderStyle,
              borderColor: attributes.borderColor,
              color: attributes.textColor,
              backgroundColor: attributes.backgroundColor,
            }}
          >
            <TrashIcon className="h-3 w-3" />
            {clearFiltersLabel}
          </button>
          {hasMultipleTags && onContentTagsMatchChange && (
            <>
              <span className="text-xs" style={{ color: metaTextColor }}>
                {matchLabel}
              </span>
              <div
                className="inline-flex overflow-hidden"
                style={{
                  borderRadius: attributes.borderRadius,
                  borderWidth: attributes.borderWidth,
                  borderStyle: attributes.borderStyle,
                  borderColor: attributes.borderColor,
                }}
              >
                <button
                  type="button"
                  onClick={() => onContentTagsMatchChange('any')}
                  className="cursor-pointer px-3 py-1 text-xs font-medium transition-colors duration-150"
                  style={{
                    color: contentTagsMatch === 'any' ? attributes.activeTextColor : attributes.textColor,
                    backgroundColor:
                      contentTagsMatch === 'any' ? attributes.activeBackgroundColor : attributes.backgroundColor,
                  }}
                >
                  {matchAnyLabel}
                </button>
                <button
                  type="button"
                  onClick={() => onContentTagsMatchChange('all')}
                  className="cursor-pointer px-3 py-1 text-xs font-medium transition-colors duration-150"
                  style={{
                    color: contentTagsMatch === 'all' ? attributes.activeTextColor : attributes.textColor,
                    backgroundColor:
                      contentTagsMatch === 'all' ? attributes.activeBackgroundColor : attributes.backgroundColor,
                    borderLeft: `${attributes.borderWidth} ${attributes.borderStyle} ${attributes.borderColor}`,
                  }}
                >
                  {matchAllLabel}
                </button>
              </div>
              <div className="relative group">
                <button type="button" className="cursor-help" aria-label={matchAriaLabel}>
                  <InformationCircleIcon className="h-4 w-4" style={{ color: metaTextColor }} />
                </button>
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover:block group-focus-within:block z-10">
                  <div className="whitespace-nowrap rounded bg-gray-900 px-2.5 py-1.5 text-xs text-white shadow-lg text-left leading-5">
                    <strong>{matchAnyLabel}</strong> — {matchTooltipAny}
                    <br />
                    <strong>{matchAllLabel}</strong> — {matchTooltipAll}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TagFilter;
